import {
  setArgoPODLogData,
  setProcessLogsList,
  setProcessLogsSummeryData,
  setProcessLogsFilters,
  setIsProcessLogsLoading,
  setBigProcessLogsFilters,
  setExportLoading,
} from "../../../features/monitoringProcessLogsSlice";
import { monitoringProcessLogsService } from "../../../services/api/monitoring-process-logs.endpoints";
import store from "../../../store";

async function getAllProcessLogsList(
  oauthTokenHeader: any,
  from_date: any,
  to_date: any,
  dispatch: any,
  run_id?: any,
  previousList: any[] = [],
  filters?: {
    status?: string[];
    run_method?: string[];
    step?: string[];
    process_id?: string[]; // Changed from process_name to process_id
    package_name?: string[];
    brand_id?: string[];
    interface_id?: string[];
    workflow_name?: string[];
  }
) {
  try {
    const cleanedFilters = filters
      ? Object.fromEntries(
          Object.entries(filters).filter(
            ([_, value]) =>
              value !== undefined &&
              value !== null &&
              (Array.isArray(value) ? value.length > 0 : true)
          )
        )
      : undefined;

    const response = await monitoringProcessLogsService.getAllProcessLogs(
      oauthTokenHeader,
      from_date,
      to_date,
      run_id,
      cleanedFilters
    );

    if (!response || !response.status) {
      throw new Error("Failed to access process logs");
    } else if (response.status === 204) {
      // No content — don't update the list
      return { reason: "No more data" };
    } else if (response.status === 200) {
      const newRecords = response?.data?.data?.valid_records || [];
      const summaries = response?.data?.data?.summaries;

      let finalList;

      // Merge with previous if run_id is provided
      if (run_id && Array.isArray(previousList)) {
        const existingRunIds = new Set(
          previousList.map((item: any) => item.run_id)
        );
        const uniqueNewRecords = newRecords.filter(
          (record: any) => !existingRunIds.has(record.run_id)
        );

        finalList = [...previousList, ...uniqueNewRecords];
      } else {
        finalList = [...newRecords];
      }

      dispatch(setProcessLogsList(finalList));
      dispatch(setProcessLogsSummeryData(summaries));
      return response?.data;
    }
  } catch (e: any) {
    console.error("Error fetching process logs:", e);
    return e?.parsedText ?? { reason: "Unknown error occurred." };
  }
}

async function exportToExcel(
  oauthTokenHeader: string,
  from_date: string,
  to_date: string,
  format: "csv" | "xlsx", // Explicit format types
  filters?: {
    status?: string[];
    run_method?: string[];
    step?: string[];
    process_id?: string[];
    package_name?: string[];
    brand_id?: string[];
    interface_id?: string[];
    workflow_name?: string[];
  }
): Promise<Blob | any> {
  // Returns Blob for CSV, any for other formats
  try {
    // Clean and validate filters
    const cleanedFilters = filters
      ? (Object.fromEntries(
          Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== null)
            .map(([key, value]) => [
              key,
              Array.isArray(value)
                ? value.filter((v) => v !== undefined && v !== null && v !== "")
                : value,
            ])
            .filter(([_, value]) =>
              Array.isArray(value) ? value.length > 0 : true
            )
        ) as typeof filters)
      : undefined;

    store.dispatch(setExportLoading(true));

    const response = await monitoringProcessLogsService.exportToExcel(
      oauthTokenHeader,
      from_date,
      to_date,
      format,
      cleanedFilters
    );

    // Handle different response types based on format
    if (format === "csv") {
      if (response instanceof Blob) {
        return response;
      }
      // Fallback for CSV if not already a Blob
      return new Blob([response], { type: "text/csv" });
    }

    // For non-CSV formats (like Excel)
    return response;
  } catch (e: any) {
    console.error("Export failed", e);

    throw new Error(`Export failed: ${e.message || "Unknown error"}`);
  } finally {
    store.dispatch(setExportLoading(false));
  }
}

async function postArgoPODLog(oauthTokenHeader: any, data: any, dispatch: any) {
  try {
    const response = await monitoringProcessLogsService.postArgoPODLogData(
      oauthTokenHeader,
      data
    );
    if (!response || !response.status) {
      throw new Error("Failed to access podlog viewer data");
    }
    if (response.status === 200) {
      dispatch(setArgoPODLogData(response?.data?.data)); // Set the podlog viewer data
    } else if (response.status === 204) {
      dispatch(setArgoPODLogData([])); //  set to empty array
    }
    return { status: response.status };
  } catch (e: any) {
    return e ? e?.parsedText : { reason: "Unknown error occurred." };
  }
}

async function getAllFilters(
  oauthTokenHeader: Record<string, string>,
  filters: any,
  dispatch: (action: any) => void
): Promise<void | { reason: string }> {
  try {
    dispatch(setIsProcessLogsLoading(true));
    const response = await monitoringProcessLogsService.getAllFiltersInParallel(
      filters,
      oauthTokenHeader
    );

    if (!response?.status || !response.data) {
      return { reason: "Failed to retrieve filters." };
    }

    dispatch(setProcessLogsFilters(response.data?.data));
  } catch (error: any) {
    const reason =
      error?.parsedText?.reason ?? error?.message ?? "Unknown error occurred.";
    return { reason };
  } finally {
    dispatch(setIsProcessLogsLoading(false));
  }
}

async function getBigFilters(
  oauthTokenHeader: Record<string, string>,
  filters: any,
  dispatch: (action: any) => void
): Promise<void | { reason: string }> {
  try {
    dispatch(setIsProcessLogsLoading(true));
    const response = await monitoringProcessLogsService.getAllFiltersInParallel(
      filters,
      oauthTokenHeader
    );

    if (!response?.status || !response.data) {
      return { reason: "Failed to retrieve filters." };
    }

    dispatch(setBigProcessLogsFilters(response.data?.data));
  } catch (error: any) {
    const reason =
      error?.parsedText?.reason ?? error?.message ?? "Unknown error occurred.";
    return { reason };
  } finally {
    dispatch(setIsProcessLogsLoading(false));
  }
}

async function getSortedData(
  oauthTokenHeader: any,
  from_date: string,
  to_date: string,
  sort_field: string,
  sort_order: "ASC" | "DESC",
  run_id?: string | number,
  filters?: {
    status?: string[];
    run_method?: string[];
    step?: string[];
    process_id?: string[];
    package_name?: string[];
    brand_id?: string[];
    interface_id?: string[];
    workflow_name?: string[];
  }
) {
  return monitoringProcessLogsService.getSortedData(
    oauthTokenHeader,
    from_date,
    to_date,
    sort_field,
    sort_order,
    run_id,
    filters
  );
}

export const ProcessLogsEndPointLogic = {
  getAllProcessLogsList,
  postArgoPODLog,
  getAllFilters,
  getBigFilters,
  exportToExcel,
  getSortedData,
};
